import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import {
  CartAddEvent,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  ThemeEvents,
} from '@theme/events';
import { Drawer } from '@theme/drawer';

/**
 * @typedef {object} CartDrawerRefs
 * @property {HTMLDialogElement} drawer - The cart drawer dialog element.
 * @property {HTMLFormElement[]} dialogForms - Forms with method="dialog" inside the drawer.
 * @property {HTMLElement} cartItems - Container for cart items.
 * @property {HTMLElement} cartItemCount - Hidden element containing cart item count.
 * @property {HTMLElement[]} quantitySelectors - The quantity selector components.
 * @property {HTMLElement} [freeShippingProgressbar] - Free shipping gauge progressbar track.
 * @property {HTMLElement} [gwpReaddContainer] - Container for re-add gift button.
 * @property {HTMLElement} [gwpReaddButton] - Re-add gift button.
 */

/**
 * @extends {Drawer}
 */
class CartDrawer extends Drawer {
  static drawerName = 'cart-drawer';
  static redirectCookieName = 'cart_drawer';

  /** @type {string | undefined} */
  sectionId;

  /** @type {AbortController | null} */
  #cartUpdateController = null;

  /** @type {boolean} */
  #gwpOperationInProgress = false;

  /** @type {string | null} */
  #previousGwpBlockId = null;

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.drawer || !(this.refs.drawer instanceof HTMLDialogElement)) return;

    this.sectionId = this.dataset.sectionId;
    this.#previousGwpBlockId = this.dataset.gwpBlockId || null;

    this.#setupThemeEditorListeners();
    this.#setupCartUpdateListener();
    this.#setupCartAddListener();
    this.#setupQuantitySelectorListener();
    this.#setupGwpReaddListener();
    this.#openFromRedirectCookie();
    this.#cleanupOrphanedGwpItems();
    this.#checkAndManageGwp();
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#restoreGwpDismissedState();
  }

  /**
   * Synchronously restores the gwp-dismissed attribute after morph,
   * based on localStorage state. No async fetch needed.
   */
  #restoreGwpDismissedState() {
    const blockId = this.dataset.gwpBlockId;
    if (!blockId) return;

    if (this.#isGwpDismissed(blockId)) {
      this.setAttribute('data-gwp-dismissed', '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (typeof Shopify !== 'undefined' && Shopify?.designMode) {
      document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);
    }

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  #setupThemeEditorListeners() {
    if (typeof Shopify !== 'undefined' && Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);
    }
  }

  #setupCartUpdateListener() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #setupCartAddListener() {
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #setupQuantitySelectorListener() {
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  #openFromRedirectCookie() {
    if (!this.#hasCookie(CartDrawer.redirectCookieName)) return;

    requestAnimationFrame(() => {
      if (this.refs.drawer && !this.refs.drawer.hasAttribute('open')) {
        this.openDrawer();
      }
      this.#clearCookie(CartDrawer.redirectCookieName);
    });
  }

  /**
   * @param {string} name
   * @returns {boolean}
   */
  #hasCookie(name) {
    const cookiePrefix = `${name}=`;
    const cookies = document.cookie ? document.cookie.split(';') : [];

    for (const rawCookie of cookies) {
      const cookie = rawCookie.trim();
      if (cookie.startsWith(cookiePrefix)) {
        return true;
      }
    }

    return false;
  }

  /**
   * @param {string} name
   */
  #clearCookie(name) {
    document.cookie = `${name}=; path=/; max-age=0; samesite=lax`;
  }

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (!this.refs.drawer) return;

    const customEvent = /** @type {CustomEvent} */ (event);
    const eventSectionId = customEvent.detail?.sectionId;
    if (eventSectionId && this.sectionId && eventSectionId === this.sectionId) {
      if (!this.refs.drawer.hasAttribute('open')) {
        this.openDrawer();
      }
    }
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (!this.refs.drawer) return;

    const customEvent = /** @type {CustomEvent} */ (event);
    const eventSectionId = customEvent.detail?.sectionId;
    if (eventSectionId && this.sectionId && eventSectionId === this.sectionId) {
      if (this.refs.drawer.hasAttribute('open')) {
        this.closeDrawer();
      }
    }
  };

  /** @param {Event} _event */
  #handleCartAdd = (_event) => {
    if (this.hasAttribute('auto-open') && this.refs.drawer && !this.refs.drawer.hasAttribute('open')) {
      this.openDrawer();
    }
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange = (event) => {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
  };

  /**
   * Handles the line item removal.
   * Called via on:click="/onLineItemRemove/{lineIndex}"
   * @param {number|Event} lineOrEvent - The line item index (from on:click) or the event object.
   * @param {Event} [event] - The event object (if line was passed as first param).
   */
  onLineItemRemove(lineOrEvent, event) {
    const line = event ? lineOrEvent : (typeof lineOrEvent === 'number' ? lineOrEvent : null);

    if (!line || typeof line !== 'number') {
      console.error('onLineItemRemove: line parameter is missing or invalid', { line, lineOrEvent, event });
      return;
    }

    this.#handleGwpManualRemoval(line);

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (!this.sectionId) return;

    if (this.#cartUpdateController) {
      this.#cartUpdateController.abort();
    }

    this.#cartUpdateController = new AbortController();
    const signal = this.#cartUpdateController.signal;

    this.#disableCartItems();
    this.#shimmerCartItemImage(config.line);

    const { line, quantity } = config;

    const cartItemsComponents = document.querySelectorAll('cart-drawer');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_change_url}`, { ...fetchConfig('json', { body }), signal })
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        const hasError = !!parsedResponseText.errors;
        const hasSections = this.sectionId && parsedResponseText.sections && parsedResponseText.sections[this.sectionId];
        const errorMessage = hasError ? parsedResponseText.errors : null;

        if (hasSections && this.sectionId) {
          const newSectionHTML = new DOMParser().parseFromString(
            parsedResponseText.sections[this.sectionId],
            'text/html'
          );

          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

          this.#updateQuantitySelectors(parsedResponseText);

          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: newCartItemCount,
              source: 'cart-drawer',
              sections: parsedResponseText.sections,
            })
          );

          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
          this.#preserveOpeningClasses();

          this.#updateCartQuantitySelectorButtonStates();
          this.#resetShimmerCartItemImage(line);
          this.#handleGwpAfterUpdate();

          if (hasError && errorMessage) {
            requestAnimationFrame(() => {
              this.#handleCartError(line, { errors: errorMessage });
            });
          }
        } else if (hasError && this.sectionId) {
          sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
            this.#updateCartQuantitySelectorButtonStates();
            this.#resetShimmerCartItemImage(line);
            if (errorMessage) {
              this.#handleCartError(line, { errors: errorMessage });
            }
          });
        } else if (hasError) {
          this.#handleCartError(line, { errors: errorMessage });
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }
        console.error(error);
      })
      .finally(() => {
        if (!signal.aborted) {
          this.#enableCartItems();
          this.#resetShimmerCartItemImage(config.line);
        }
      });
  }

  /**
   * Applies shimmer effect to cart item image during loading
   * @param {number} line - The cart line index
   */
  #shimmerCartItemImage(line) {
    const imageWrapper = this.refs[`cartItemImage-${line}`];
    if (imageWrapper instanceof HTMLElement) {
      imageWrapper.setAttribute('shimmer', '');
    }
  }

  /**
   * Removes shimmer effect from cart item image
   * @param {number} line - The cart line index
   */
  #resetShimmerCartItemImage(line) {
    const imageWrapper = this.refs[`cartItemImage-${line}`];
    if (imageWrapper instanceof HTMLElement) {
      imageWrapper.removeAttribute('shimmer');
    }
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelectors = this.refs.quantitySelectors;
    const quantitySelector = Array.isArray(quantitySelectors) ? quantitySelectors[line - 1] : null;
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) return;

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
    }
    if (cartItemErrorContainer instanceof HTMLElement) {
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.#setFreeShippingProgressbarBusy(true);
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.#setFreeShippingProgressbarBusy(false);
  }

  /**
   * Updates the free shipping gauge busy state for assistive technologies.
   * @param {boolean} isBusy
   */
  #setFreeShippingProgressbarBusy(isBusy) {
    const progressbar = this.refs.freeShippingProgressbar;
    if (!(progressbar instanceof HTMLElement)) return;

    progressbar.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  /**
   * Réapplique les classes d'ouverture après un morphing si le drawer est ouvert.
   */
  #preserveOpeningClasses() {
    if (this.refs.drawer?.hasAttribute('open')) {
      this.refs.drawer.classList.add('cart-drawer--opening');
      this.refs.drawer.classList.add('drawer-shell--opening');
    }
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    const cartItems = this.refs.cartItems;
    if (!cartItems || Array.isArray(cartItems)) return;

    const cartQuantitySelectors = cartItems.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /** @param {Event} event */
  #handleCartUpdate = async (event) => {
    if (!this.sectionId) return;
    if (event.target === this) return;

    if (!(event instanceof CartAddEvent) && !(event instanceof CartUpdateEvent)) return;

    const sections = event.detail?.data?.sections;
    if (!sections || !this.sectionId) return;

    const sectionId = this.sectionId;

    try {
      if (sections[sectionId]) {
        await morphSection(sectionId, sections[sectionId]);
        this.#preserveOpeningClasses();
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        await sectionRenderer.renderSection(sectionId, { cache: false });
      }

      this.#handleGwpAfterUpdate();
    } catch (error) {
      console.error('Error updating cart drawer:', error);
    }
  };

  // ---- Gift With Purchase (GWP) ----

  #setupGwpReaddListener() {
    this.addEventListener('click', this.#onGwpReaddDelegated);
  }

  /**
   * Removes GWP items from cart whose block ID doesn't match the current config.
   * Handles the case where a GWP block was removed/disabled but the gift is still in cart.
   */
  async #cleanupOrphanedGwpItems() {
    const activeBlockId = this.dataset.gwpBlockId;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await response.json();

      for (const item of cart.items) {
        if (!item.properties || !item.properties._gwp_block_id) continue;

        if (item.properties._gwp_block_id !== activeBlockId) {
          const lineIndex = cart.items.indexOf(item) + 1;
          console.debug('GWP: removing orphaned gift', { blockId: item.properties._gwp_block_id, line: lineIndex });
          await this.#removeGwpItem(item, cart);
          return;
        }
      }
    } catch (error) {
      console.error('GWP: cleanup error', error);
    }
  }

  /**
   * Called after cart update/morph to check GWP state.
   */
  #handleGwpAfterUpdate() {
    if (this.#gwpOperationInProgress) return;

    const currentBlockId = this.dataset.gwpBlockId;

    if (this.#previousGwpBlockId && !currentBlockId) {
      this.#removeGwpItemsByBlockId(this.#previousGwpBlockId);
      this.#previousGwpBlockId = null;
      return;
    }

    this.#previousGwpBlockId = currentBlockId || null;
    this.#checkAndManageGwp();
  }

  async #checkAndManageGwp() {
    const threshold = parseInt(this.dataset.gwpThreshold ?? '', 10);
    const variantId = this.dataset.gwpVariantId;
    const blockId = this.dataset.gwpBlockId;
    const isAvailable = this.dataset.gwpAvailable === 'true';

    if (isNaN(threshold) || !variantId || !blockId) {
      console.debug('GWP: missing config', { threshold, variantId, blockId });
      return;
    }

    if (!isAvailable) {
      console.debug('GWP: gift product not available');
      await this.#removeGwpItemsByBlockId(blockId);
      return;
    }

    let cart;
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      cart = await response.json();
    } catch (error) {
      console.error('GWP: failed to fetch cart', error);
      return;
    }

    const gwpLineItem = cart.items.find(/** @param {any} item */ (item) =>
      item.properties && item.properties._gwp_block_id === blockId
    );

    const cartTotalWithoutGifts = cart.items.reduce(/** @param {number} total @param {any} item */ (total, item) => {
      if (item.properties && item.properties._gwp_block_id) return total;
      return total + item.final_line_price;
    }, 0);

    const thresholdMet = cartTotalWithoutGifts >= threshold;
    const isDismissed = this.#isGwpDismissed(blockId);

    console.debug('GWP: check', { cartTotalWithoutGifts, threshold, thresholdMet, gwpInCart: !!gwpLineItem, isDismissed });

    if (!thresholdMet) {
      this.#clearGwpDismissed(blockId);

      if (gwpLineItem) {
        await this.#removeGwpItem(gwpLineItem, cart);
      }
      this.#hideGwpReaddButton();
      return;
    }

    if (thresholdMet && !gwpLineItem && !isDismissed) {
      await this.#addGwpItem(variantId, blockId);
      return;
    }

    if (thresholdMet && !gwpLineItem && isDismissed) {
      this.#showGwpReaddButton();
    }
  }

  /**
   * @param {string} variantId
   * @param {string} blockId
   */
  async #addGwpItem(variantId, blockId) {
    if (this.#gwpOperationInProgress) return;
    this.#gwpOperationInProgress = true;

    console.debug('GWP: adding gift', { variantId, blockId });

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('properties[_gwp_block_id]', blockId);
      if (this.sectionId) {
        formData.append('sections', this.sectionId);
      }

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });

      const result = await response.json();
      console.debug('GWP: add response', result);

      if (result.status) {
        console.error('GWP: add failed', result.description);
        return;
      }

      if (result.sections && this.sectionId) {
        this.dispatchEvent(
          new CartAddEvent({}, this.sectionId, {
            source: 'gwp-auto-add',
            sections: result.sections,
          })
        );

        await morphSection(this.sectionId, result.sections[this.sectionId]);
        this.#preserveOpeningClasses();
      }
    } catch (error) {
      console.error('GWP: Error adding gift item', error);
    } finally {
      this.#gwpOperationInProgress = false;
    }
  }

  /**
   * @param {any} gwpLineItem
   * @param {{items: any[]}} cart
   */
  async #removeGwpItem(gwpLineItem, cart) {
    const lineIndex = cart.items.indexOf(gwpLineItem) + 1;
    if (lineIndex < 1) return;
    if (this.#gwpOperationInProgress) return;
    this.#gwpOperationInProgress = true;

    try {
      const body = JSON.stringify({
        line: lineIndex,
        quantity: 0,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, {
        ...fetchConfig('json', { body }),
      });

      const result = await response.json();

      if (result.sections && this.sectionId) {
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            source: 'gwp-auto-remove',
            sections: result.sections,
          })
        );

        await morphSection(this.sectionId, result.sections[this.sectionId]);
        this.#preserveOpeningClasses();
      }
    } catch (error) {
      console.error('GWP: Error removing gift item', error);
    } finally {
      this.#gwpOperationInProgress = false;
    }
  }

  /**
   * @param {string} blockId
   */
  async #removeGwpItemsByBlockId(blockId) {
    if (this.#gwpOperationInProgress) return;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await response.json();

      const gwpItem = cart.items.find(/** @param {any} item */ (item) =>
        item.properties && item.properties._gwp_block_id === blockId
      );

      if (gwpItem) {
        await this.#removeGwpItem(gwpItem, cart);
      }
    } catch (error) {
      console.error('GWP: Error cleaning up gift items', error);
    }
  }

  /**
   * Handles manual removal of a GWP item by setting the dismissed flag.
   * Called from onLineItemRemove when the removed item is a GWP item.
   * @param {number} line
   */
  #handleGwpManualRemoval(line) {
    const cartItemRemoveButton = this.querySelector(
      `[on\\:click="/onLineItemRemove/${line}"][data-gwp-item]`
    );

    if (!cartItemRemoveButton) return false;

    const blockId = this.dataset.gwpBlockId;
    if (blockId) {
      this.#setGwpDismissed(blockId);
    }
    return true;
  }

  /** @param {string} blockId */
  #isGwpDismissed(blockId) {
    try {
      return localStorage.getItem(`gwp_dismissed_${blockId}`) === 'true';
    } catch {
      return false;
    }
  }

  /** @param {string} blockId */
  #setGwpDismissed(blockId) {
    try {
      localStorage.setItem(`gwp_dismissed_${blockId}`, 'true');
    } catch {
      // localStorage not available
    }
  }

  /** @param {string} blockId */
  #clearGwpDismissed(blockId) {
    try {
      localStorage.removeItem(`gwp_dismissed_${blockId}`);
    } catch {
      // localStorage not available
    }
  }

  /** @param {Event} event */
  #onGwpReaddDelegated = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest('[ref="gwpReaddButton"]')) return;

    const blockId = this.dataset.gwpBlockId;
    const variantId = this.dataset.gwpVariantId;

    if (!blockId || !variantId) return;

    this.#clearGwpDismissed(blockId);
    this.#hideGwpReaddButton();
    this.#addGwpItem(variantId, blockId);
  };

  #showGwpReaddButton() {
    this.setAttribute('data-gwp-dismissed', '');
  }

  #hideGwpReaddButton() {
    this.removeAttribute('data-gwp-dismissed');
  }
}

customElements.define('cart-drawer', CartDrawer);
